<script setup lang="ts">
// const { locale, locales, t } = useI18n()
const display = useDisplay()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const contractStore = useContractStore()
const messageStore = useMessageStore()
const campaignStore = useCampaignStore()

// 선택된 메뉴 상태
const selectedMenu = ref('')
const selectedTab = ref('home')

// 쿼리 파라미터와 탭 매핑
const queryToTabMap: Record<string, string> = {
  received_message: 'message',
  contract_manage: 'contract_manage',
  campaign_manage: 'campaign_manage',
  scrap: 'scrap',
  mypage: 'mypage',
  connect_sns: 'connect_sns',
  campaign_history: 'campaign_history',
  guide: 'guide',
  content_manage: 'content_manage',
  advertiser_dashboard: 'advertiser_dashboard',
}

const tabToQueryMap: Record<string, string> = {
  home: '',
  message: 'received_message',
  contract_manage: 'contract_manage',
  campaign_manage: 'campaign_manage',
  scrap: 'scrap',
  mypage: 'mypage',
  connect_sns: 'connect_sns',
  campaign_history: 'campaign_history',
  guide: 'guide',
  content_manage: 'content_manage',
  advertiser_dashboard: 'advertiser_dashboard',
}

// 초기화: 현재 쿼리 파라미터에 따라 탭 설정
onMounted(() => {
  const currentMenu = route.query.menu as string
  if (currentMenu && queryToTabMap[currentMenu]) {
    selectedTab.value = queryToTabMap[currentMenu]
  }
  else {
    selectedTab.value = 'home'
  }
})

// 탭 변경 감지하여 라우팅 처리
watch(selectedTab, (newTab) => {
  const queryParam = tabToQueryMap[newTab]
  if (queryParam) {
    router.push({ query: { menu: queryParam } })
  }
  else {
    router.push({ query: {} })
  }
})

function handleMenuClick(menuName: string) {
  selectedMenu.value = menuName

  // URL 쿼리 파라미터 업데이트
  const menuQueryMap: Record<string, string> = {
    '받은 메시지': 'received_message',
    '계약 관리': 'contract_manage',
    '캠페인 관리': 'campaign_manage',
    '스크랩': 'scrap',
    '마이페이지': 'mypage',
    'SNS 채널 연결': 'connect_sns',
    '캠페인 히스토리': 'campaign_history',
    '이용가이드': 'guide',
    '등록 콘텐츠': 'content_manage',
    '성과 대시보드': 'advertiser_dashboard',
  }

  if (menuQueryMap[menuName]) {
    if (menuName === '계약 관리') {
      contractStore.currentView = 'list' // 계약 관리 진입 시 목록 뷰로 초기화
    }
    else if (menuName === '받은 메시지') {
      messageStore.currentView = 'list' // 받은 메시지 진입 시 목록 뷰로 초기화
    }
    else if (menuName === '캠페인 관리') {
      // 캠페인 관리 진입 시 필요한 초기화 작업 수행
      campaignStore.currentView = 'list'
    }
    router.push({ query: { menu: menuQueryMap[menuName] } })
  }
  else if (menuName === '마이 홈') {
    router.push({ query: {} }) // 마이 홈은 쿼리 파라미터 제거
  }
}
</script>

<template>
  <div v-if="userStore.userStoreData">
    <!-- 데스크톱: 세로 사이드바 -->
    <v-navigation-drawer v-if="display.mdAndUp.value" permanent>
      <v-card>
        <div class="text-center">
          <v-chip class="bg-c2" style="margin-top:50px;">
            {{ userStore.userStoreData?.type === 'creator' ? '크리에이터' : '광고주' }}
          </v-chip>
        </div>
        <v-card-actions>
          <v-list-item class="w-100">
            <template #prepend>
              <v-avatar
                color="c10"
                :alt="`${userStore.userStoreData?.name || userStore.userStoreData?.nickname || '사용자'} 프로필 이미지`"
              >
                <v-img
                  :src="userStore.userStoreData?.sign?.profileImageUrl"
                  :lazy-src="userStore.userStoreData?.sign?.profileImageUrl"
                />
              </v-avatar>
            </template>

            <v-list-item-title>
              <v-text class="font-weight-bold text-h6">
                {{ userStore.userStoreData?.name || userStore.userStoreData?.nickname || '사용자' }}님
              </v-text>
            </v-list-item-title>

            <v-list-item-subtitle>
              <div class="rating-line">
                <span class="icon" style="font-size:18px;color:#888;">&#9733;</span>
                <span>나의 평점</span>
                <span class="rating-score text-subtitle-2 text-blue">{{ userStore.userStoreData?.ratingAverage ? userStore.userStoreData.ratingAverage.toFixed(1) : '0.0' }}</span>
              </div>
            </v-list-item-subtitle>
          </v-list-item>
        </v-card-actions>
      </v-card>
      <v-btn variant="tonal" class="d-block mx-auto" block @click="handleMenuClick('마이 홈')">
        마이 홈
      </v-btn>

      <v-list>
        <v-list-item>
          <v-list-item-title class="text-subtitle-2 text-c9">
            활동관리
          </v-list-item-title>
        </v-list-item>
        <v-list-item active-color="c2" rounded="xl" @click="handleMenuClick('계약 관리')">
          <v-list-item-title class="font-weight-medium text-subtitle-1 ml-3">
            계약관리
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="handleMenuClick('받은 메시지')">
          <v-list-item-title class="text-subtitle-1 font-weight-medium ml-3">
            받은 메시지
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="handleMenuClick('스크랩')">
          <!-- v-if="userStore.userStoreData?.type === 'creator'" -->
          <v-list-item-title class="text-subtitle-1 font-weight-medium ml-3">
            스크랩
          </v-list-item-title>
        </v-list-item>
        <v-list-item v-if="userStore.userStoreData?.type === 'advertiser'" @click="handleMenuClick('성과 대시보드')">
          <v-list-item-title class="text-subtitle-1 font-weight-medium ml-3">
            성과 대시보드
          </v-list-item-title>
        </v-list-item>
        <v-list-item v-if="userStore.userStoreData?.type === 'advertiser'" @click="handleMenuClick('캠페인 관리')">
          <v-list-item-title class="text-subtitle-1 font-weight-medium ml-3">
            캠페인 관리
          </v-list-item-title>
        </v-list-item>
        <v-list-item>
          <v-list-item-title class="text-subtitle-2 text-c9">
            계정관리
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="handleMenuClick('마이페이지')">
          <v-list-item-title class="text-subtitle-1 font-weight-medium ml-3">
            마이페이지
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="handleMenuClick('SNS 채널 연결')">
          <v-list-item-title class="text-subtitle-1 font-weight-medium ml-3">
            SNS 채널 연결
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="handleMenuClick('캠페인 히스토리')">
          <v-list-item-title class="text-subtitle-1 font-weight-medium ml-3">
            캠페인 히스토리
          </v-list-item-title>
        </v-list-item>
        <v-list-item>
          <v-list-item-title class="text-subtitle-2 text-c9">
            고객센터
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="handleMenuClick('이용가이드')">
          <v-list-item-title class="text-subtitle-1 font-weight-medium ml-3">
            이용가이드
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <!-- 모바일: 가로 탭 -->
    <v-container v-else class="mobile-nav-container">
      <v-tabs
        v-model="selectedTab"
        show-arrows
        slider-color="c2"
        class="text-center"
      >
        <v-tab value="home">
          홈
        </v-tab>
        <v-tab value="contract_manage">
          계약관리
        </v-tab>
        <v-tab value="message">
          받은메시지
        </v-tab>
        <v-tab value="scrap">
          <!-- v-if="userStore.userStoreData?.type === 'creator'" -->
          스크랩
        </v-tab>
        <v-tab v-if="userStore.userStoreData?.type === 'advertiser'" value="advertiser_dashboard">
          성과대시보드
        </v-tab>
        <v-tab v-if="userStore.userStoreData?.type === 'advertiser'" value="campaign_manage">
          캠페인관리
        </v-tab>
        <v-tab value="mypage">
          마이페이지
        </v-tab>
        <v-tab value="connect_sns">
          SNS연결
        </v-tab>
        <v-tab value="campaign_history">
          히스토리
        </v-tab>
        <v-tab value="guide">
          가이드
        </v-tab>
      </v-tabs>
    </v-container>
  </div>
</template>

<style scoped>
.rating-line {
  display: flex;
  align-items: center;
  gap: 4px;
}

.mobile-nav-container {
  position: fixed;
  top: 85px;

  left: 0;
  right: 0;
  z-index: 999;
  background: white;
  border-bottom: 1px solid #e0e0e0;
  padding: 8px 16px;
}
</style>
